import { validateId } from '~/repositories/utils';
import { communicationsServiceUrl } from '~/utils/env';
import { urlWithQuery } from '~/utils/url';
import { BaseRestRepository } from './baseRestRepository';

const LETTER_TEMPLATES_URL = `${communicationsServiceUrl()}/company-portal/letter-templates`;

export interface LetterTemplateGroupDto {
  id: string;
  name: string;
  created: string;
  updated: string;
}

export interface CreateLetterTemplateGroupDto {
  name: string;
}

export interface UpdateLetterTemplateGroupDto {
  name: string;
}

export type CommunicationChannel = 'Tenant' | 'Owner' | 'Vendor' | 'Lease' | 'MaintenanceRequest' | 'WorkOrder';

export interface LetterTemplateDto {
  id: string;
  letterTemplateGroupId: string | null;
  name: string;
  communicationChannels: CommunicationChannel[];
  emailSubject: string | null;
  emailBody: string | null;
  chatContent: string | null;
  created: string;
  updated: string;
}

export interface CreateLetterTemplateDto {
  name: string;
  letterTemplateGroupId?: string | null;
  communicationChannels: CommunicationChannel[];
  emailSubject?: string | null;
  emailBody?: string | null;
  chatContent?: string | null;
}

export interface UpdateLetterTemplateDto {
  name?: string;
  letterTemplateGroupId?: string | null;
  communicationChannels?: CommunicationChannel[];
  emailSubject?: string | null;
  emailBody?: string | null;
  chatContent?: string | null;
}

export interface PlaceholderDefinitionDto {
  key: string;
  displayName: string;
  description: string;
}

export interface PlaceholderDefinitionsDto {
  items: PlaceholderDefinitionDto[];
}

export interface ValidatePlaceholdersRequestDto {
  content: string;
  communicationChannels: CommunicationChannel[];
}

export interface ValidatePlaceholdersResponseDto {
  isValid: boolean;
  invalidPlaceholders: string[];
}

export interface RenderLetterTemplateRequestDto {
  channel: CommunicationChannel;
  entityId: string;
  messageType: 'Email' | 'Chat';
}

export interface RenderLetterTemplateResponseDto {
  subject: string | null;
  body: string;
}

export interface PaginationDto<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export type LetterTemplateGroupFilter = {
  page?: number;
  pageSize?: number;
  search?: string;
};

export type LetterTemplateFilter = {
  page?: number;
  pageSize?: number;
  channels?: CommunicationChannel[];
  letterTemplateGroupId?: string | null;
};

export type PlaceholderFilter = {
  channels?: CommunicationChannel[];
};

export class LetterTemplateRepository extends BaseRestRepository {
  public async getGroups(filter?: LetterTemplateGroupFilter): Promise<PaginationDto<LetterTemplateGroupDto>> {
    const url = urlWithQuery(`${LETTER_TEMPLATES_URL}/groups`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getGroup(id: string): Promise<LetterTemplateGroupDto> {
    validateId(id, 'Letter template group');
    const response = await this.fetchWithAuth(`${LETTER_TEMPLATES_URL}/groups/${id}`);
    return this.getJsonResponse(response);
  }

  public async createGroup(payload: CreateLetterTemplateGroupDto): Promise<LetterTemplateGroupDto> {
    const response = await this.fetchWithAuth(`${LETTER_TEMPLATES_URL}/groups`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updateGroup(id: string, payload: UpdateLetterTemplateGroupDto): Promise<LetterTemplateGroupDto> {
    validateId(id, 'Letter template group');
    const response = await this.fetchWithAuth(`${LETTER_TEMPLATES_URL}/groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteGroup(id: string): Promise<void> {
    validateId(id, 'Letter template group');
    await this.fetchWithAuth(`${LETTER_TEMPLATES_URL}/groups/${id}`, {
      method: 'DELETE',
    });
  }

  public async getTemplates(filter?: LetterTemplateFilter): Promise<PaginationDto<LetterTemplateDto>> {
    const url = urlWithQuery(LETTER_TEMPLATES_URL, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }

  public async getTemplate(id: string): Promise<LetterTemplateDto> {
    validateId(id, 'Letter template');
    const response = await this.fetchWithAuth(`${LETTER_TEMPLATES_URL}/${id}`);
    return this.getJsonResponse(response);
  }

  public async createTemplate(payload: CreateLetterTemplateDto): Promise<LetterTemplateDto> {
    const response = await this.fetchWithAuth(LETTER_TEMPLATES_URL, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async updateTemplate(id: string, payload: UpdateLetterTemplateDto): Promise<LetterTemplateDto> {
    validateId(id, 'Letter template');
    const response = await this.fetchWithAuth(`${LETTER_TEMPLATES_URL}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async deleteTemplate(id: string): Promise<void> {
    validateId(id, 'Letter template');
    await this.fetchWithAuth(`${LETTER_TEMPLATES_URL}/${id}`, {
      method: 'DELETE',
    });
  }

  public async renderTemplate(id: string, payload: RenderLetterTemplateRequestDto): Promise<RenderLetterTemplateResponseDto> {
    validateId(id, 'Letter template');
    const response = await this.fetchWithAuth(`${LETTER_TEMPLATES_URL}/${id}/render`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async validatePlaceholders(payload: ValidatePlaceholdersRequestDto): Promise<ValidatePlaceholdersResponseDto> {
    const response = await this.fetchWithAuth(`${LETTER_TEMPLATES_URL}/validate-placeholders`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return this.getJsonResponse(response);
  }

  public async getPlaceholders(filter?: PlaceholderFilter): Promise<PlaceholderDefinitionsDto> {
    const url = urlWithQuery(`${LETTER_TEMPLATES_URL}/placeholders`, filter);
    const response = await this.fetchWithAuth(url);
    return this.getJsonResponse(response);
  }
}

export const letterTemplateRepository = new LetterTemplateRepository();
